import { Box, Paper, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { fetchStatisticsGroupInsights } from "../statisticsGroupInsightsApi";
import {
  buildStatisticsQueryFilters,
  type StatisticsFilters,
  type StatisticsGroupInsightsQueryBody,
  type StatisticsGroupSizeBucketKey,
} from "../types";
import { KpiPieChart, type PieDatum } from "./KpiPieChart";

interface StatisticsGroupInsightsSectionProps {
  filters: StatisticsFilters;
  /** When false, skips the network request (used to defer behind KPI load). */
  enabled?: boolean;
}

function defaultFrom(): Dayjs {
  return dayjs().subtract(29, "day").startOf("day");
}

function defaultTo(): Dayjs {
  return dayjs().startOf("day");
}

const cardSx = {
  flex: 1,
  minWidth: 0,
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
} as const;

const SIZE_BUCKET_ORDER: StatisticsGroupSizeBucketKey[] = [
  "up_to_10",
  "10_20",
  "20_30",
  "30_40",
  "40_50",
  "50plus",
];

export const StatisticsGroupInsightsSection: FC<
  StatisticsGroupInsightsSectionProps
> = ({ filters, enabled = true }) => {
  const { currOrganization } = useOrganization();
  const [from, setFrom] = useState<Dayjs>(defaultFrom);
  const [to, setTo] = useState<Dayjs>(defaultTo);

  const { dateFrom, dateTo } = useMemo(() => {
    let a = from.startOf("day");
    let b = to.startOf("day");
    if (a.isAfter(b)) {
      const t = a;
      a = b;
      b = t;
    }
    return {
      dateFrom: a.format("YYYY-MM-DD"),
      dateTo: b.format("YYYY-MM-DD"),
    };
  }, [from, to]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "statistics-group-insights",
      currOrganization?.id,
      filters,
      dateFrom,
      dateTo,
    ],
    queryFn: () => {
      const base = buildStatisticsQueryFilters(currOrganization!.id, filters);
      const body: StatisticsGroupInsightsQueryBody = {
        ...base,
        dateFrom,
        dateTo,
      };
      return fetchStatisticsGroupInsights(body);
    },
    enabled: enabled && !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
  });

  const preferredRegionPie: PieDatum[] = useMemo(() => {
    return (data?.preferredRegions ?? []).map((r) => ({
      name: r.regionLabel,
      value: Number(r.count ?? 0),
    }));
  }, [data?.preferredRegions]);

  const sizePie: PieDatum[] = useMemo(() => {
    const byKey = new Map(
      (data?.sizeBuckets ?? []).map((r) => [r.bucketKey, r]),
    );
    return SIZE_BUCKET_ORDER.map((key) => {
      const row = byKey.get(key);
      return {
        name: row?.bucketLabel ?? key,
        value: Number(row?.count ?? 0),
      };
    });
  }, [data?.sizeBuckets]);

  const repeatPie: PieDatum[] = useMemo(() => {
    return (data?.repeat ?? []).map((r) => ({
      name: r.bucketLabel,
      value: Number(r.count ?? 0),
    }));
  }, [data?.repeat]);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight={600} color="primary">
        סטטיסטיקות קבוצות מאורגנות
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
      >
        <DatePicker
          label="מתאריך"
          value={from}
          onChange={(v) => v && setFrom(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        <DatePicker
          label="עד תאריך"
          value={to}
          onChange={(v) => v && setTo(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        <Typography variant="body2" fontWeight={600} color="primary">
          קבוצות פעילות:{" "}
          {isLoading ? "…" : (data?.totalGroups ?? 0).toLocaleString("he-IL")}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="primary">
          חברים בקבוצות:{" "}
          {isLoading ? "…" : (data?.totalMembers ?? 0).toLocaleString("he-IL")}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="primary">
          מאצ'ים בטווח:{" "}
          {isLoading
            ? "…"
            : (data?.approvedParticipationsInRange ?? 0).toLocaleString(
                "he-IL",
              )}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="primary">
          קבוצות חדשות בטווח:{" "}
          {isLoading
            ? "…"
            : (data?.newGroupsInRange ?? 0).toLocaleString("he-IL")}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 720 }}>
        אזור מועדף: לפי שדה אזורי התנדבות מועדפים בקבוצה (קבוצה עם מספר אזורים
        נספרת בכל אזור). כמות מתנדבים: לפי גודל הקבוצה (שדה size). התנדבויות
        בטווח: סכום מאצ'ים (גודל קבוצה לפי size); חזרת
        קבוצות: לכל קבוצה נספרות משימות מאושרות בטווח — הגרף מפלח לפי מספר
        ההשתתפויות (0, 1, …, 6+). סכום העוגה שווה למספר הקבוצות הפעילות.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "start",
          width: "100%",
        }}
      >
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart
            title="אזור מועדף"
            data={preferredRegionPie}
            emptyLabel="אין נתונים"
          />
        </Paper>
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart
            title="כמות מתנדבים"
            data={sizePie}
            emptyLabel="אין נתונים"
          />
        </Paper>
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart
            title="חזרה (לפי מספר השתתפויות בטווח)"
            data={repeatPie}
            emptyLabel="אין נתונים"
          />
        </Paper>
      </Box>
    </Stack>
  );
};
